import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { PDF_FINANCIAL_SITUATION_PARTS, type PdfFinancialSituationPart } from "@repo/shared/types";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { CurrencyDisplayField } from "./currency-display-field";
import { T } from "./financial-situation-form.i18n";
import type { ComponentFormProps } from "./types";

export type FinancialSituationConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  /** Parts to print; absent = printed. */
  parts?: Partial<Record<PdfFinancialSituationPart, boolean>>;
};

export function FinancialSituationForm({
  config,
  onChange,
}: ComponentFormProps<FinancialSituationConfig>) {
  const t = useT(T);
  const parts = config.parts ?? {};

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="financial-situation-title">{t.title}</Label>
        <Input
          id="financial-situation-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="financial-situation-description">{t.description}</Label>
        <Textarea
          id="financial-situation-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
      </div>

      <CurrencyDisplayField
        blockType="financial_situation"
        value={config.currencyDisplay}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="space-y-2 border-t pt-4">
        <Label>{t.include}</Label>
        <p className="text-xs text-muted-foreground">{t.includeHelp}</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-1">
          {PDF_FINANCIAL_SITUATION_PARTS.map((part) => (
            <div key={part} className="flex items-center space-x-2">
              <Checkbox
                id={`financial-situation-${part}`}
                checked={parts[part] !== false}
                onCheckedChange={(checked) =>
                  onChange({ ...config, parts: { ...parts, [part]: checked === true } })
                }
              />
              <Label htmlFor={`financial-situation-${part}`} className="font-normal cursor-pointer">
                {t[part]}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function getDefaultFinancialSituationConfig(): FinancialSituationConfig {
  return {
    title: "Financial situation",
    parts: Object.fromEntries(PDF_FINANCIAL_SITUATION_PARTS.map((part) => [part, true])),
  };
}
